import { IPromise } from 'angular';
import { call, put } from 'redux-saga/effects';
import { Messages } from '../../../codegen/Localize';
import { ITagWithCount } from '../../../ts/directives/tbTagsInput';
import { TagsHelper } from '../../../ts/helpers/TagsInputHelper';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { Set } from '../../../ts/models/Set';
import { Databases } from '../../../ts/resources/Databases';
import { Tables } from '../../../ts/resources/Tables';
import { getCatalogContentTags } from '../../../ts/restApi/catalogContentTags/getCatalogContentTags';
import { IResult } from '../../../ts/ServerApi/types/IResponse';
import { ChangeContentTagsAction } from '../../../ts/services/ChangeContentTagsAction';
import { ConfirmActionDialog, IConfirmActionDialogOptions } from '../../../ts/services/ConfirmActionDialog';
import { IConfirmDialogTagsScope } from '../../../ts/services/ContentActions';
import { requestFailed } from '../../actions/RequestFailedAction';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { SetExternalContentTagsAction, SetTagsAction } from '../../actions/spaces/SetTagsActions';
import { ServiceLocator } from '../../ServiceLocator';
import { reloadSpaceIfUpdatingCurrentSpace } from './content/RefreshContentSagas';
import { replaceDatasourceIdWithConnectionIdForMixedContent } from './SpacesSagaHelpers';

export function* handleSetTags(action: SetTagsAction) {
  try {
    //Hack to mitigate Datasource API expecting DataConnectionId
    let mutatableContentCopy: IContentInfo[] = [];
    mutatableContentCopy = yield call(replaceDatasourceIdWithConnectionIdForMixedContent, action.content);
    //endHack

    yield call(changeContentTags, mutatableContentCopy);

    // reload the space if we changed its tags so that opening the tags dialog again will have the updated tags info
    const maybeReloaded = yield call(reloadSpaceIfUpdatingCurrentSpace, action.content);
    if (!maybeReloaded) {
      yield put(requestSpaceContents());
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function changeContentTags(contentCollection: IContentInfo[]): ng.IPromise<IResult> {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<ChangeContentTagsAction>('ChangeContentTagsAction').showChangeTagsForMixedContentDialog(contentCollection, context);
}

export function* handleSetExternalContentTags(action: SetExternalContentTagsAction) {
  try {
    const tags: ReadonlyArray<string> = yield call(getCatalogContentTags, { luid: action.item.luid, contentType: action.item.contentType });
    yield call(openChangeExternalTagsDialog, action.item, tags);

    const maybeReloaded = yield call(reloadSpaceIfUpdatingCurrentSpace, [action.item]);
    if (!maybeReloaded) {
      yield put(requestSpaceContents());
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function openChangeExternalTagsDialog(item: SetExternalContentTagsAction['item'], existingTags: ReadonlyArray<string>): IPromise<unknown> {
  const confirmActionDialog = ServiceLocator.get<ConfirmActionDialog>('ConfirmActionDialog');

  const previousTagSet: Set<ITagWithCount> = new Set(t => t.name);
  previousTagSet.addItems(existingTags.map(t => { return { name: t } as ITagWithCount; }));

  const scope = buildScope(previousTagSet, item.name || '');
  const dialogConfig: IConfirmActionDialogOptions = {
    scope,
    bodyTemplateUrl: 'confirmActionDialogTags.html',
    title: Messages.changeTags(),
    actionName: Messages.changeTagsConfirm(),
    position: { reserveHeight: 450 },
    action: () => {
      const editTags = TagsHelper.getTagsChange(previousTagSet, scope.tags.model);
      const changeTagsArg = {
        name: item.name,
        luid: item.luid,
        tagsToAdd: editTags.addTags,
        tagsToRemove: editTags.removeTags
      };

      switch (item.contentType) {
        case 'database':
          return ServiceLocator.get<Databases>('Databases').changeTags(changeTagsArg);
        case 'table':
          return ServiceLocator.get<Tables>('Tables').changeTags(changeTagsArg);
        default:
          throw 'implement tables and columns and throw otherwise';
      }
    }
  };

  return confirmActionDialog.open(dialogConfig).result.finally(() => scope.$destroy());
}

function buildScope(itemSet: Set<ITagWithCount>, name: string): IConfirmDialogTagsScope {
  const angularContext = ServiceLocator.getAngularActionContext();
  const scope = <IConfirmDialogTagsScope>angularContext.scope.$new();

  scope.description = Messages.changeTagsContentDescription({NAME: name});

  scope.tags = {
    model: itemSet.toArray() // TODO: TagsHelper.unquote(t.name) maybe?
  };

  return scope;
}
